/**
 * @file     useCsrf.ts
 * @purpose  Fetches a CSRF token from /api/csrf and provides it for form submissions. Caches the token for 50 minutes.
 */
'use client';

import { useCallback, useRef } from 'react';

const TOKEN_CACHE_MS = 50 * 60_000; // 50 minutes (tokens expire at 60)

let cachedToken: string | null = null;
let cachedAt = 0;

/**
 * Hook that provides a getCsrfToken function.
 * Fetches once and caches for 50 minutes.
 */
export function useCsrf() {
  const fetchingRef = useRef(false);

  const getCsrfToken = useCallback(async (): Promise<string> => {
    // Return cached token if fresh
    if (cachedToken && Date.now() - cachedAt < TOKEN_CACHE_MS) {
      return cachedToken;
    }

    // Prevent concurrent fetches
    if (fetchingRef.current) {
      // Wait for the in-flight fetch
      await new Promise(r => setTimeout(r, 100));
      if (cachedToken) return cachedToken;
    }

    fetchingRef.current = true;
    try {
      const res = await fetch('/api/csrf');
      const data = await res.json();
      cachedToken = data.token;
      cachedAt = Date.now();
      return cachedToken!;
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  return { getCsrfToken };
}

/**
 * Standalone function for non-hook contexts (e.g., autoSignIn service).
 */
export async function fetchCsrfToken(): Promise<string> {
  if (cachedToken && Date.now() - cachedAt < TOKEN_CACHE_MS) {
    return cachedToken;
  }
  const res = await fetch('/api/csrf');
  const data = await res.json();
  cachedToken = data.token;
  cachedAt = Date.now();
  return cachedToken!;
}

/** Clear the in-memory CSRF cache so the next call fetches a fresh token. */
export function invalidateCsrfToken(): void {
  cachedToken = null;
  cachedAt = 0;
}
